
import { supabase } from "@/lib/supabase";

// Set a user as admin
export const setUserAsAdmin = async (email: string) => {
  try {
    // First, find the user by email
    const { data: user, error: userError } = await supabase
      .from('profiles')
      .select('id, full_name')
      .ilike('full_name', `%${email}%`)
      .single();
    
    if (userError) {
      console.error("Error finding user:", userError);
      return { success: false, message: "User not found" };
    }
    
    if (!user) {
      return { success: false, message: "User not found" };
    }
    
    // Check if user already has admin role
    const { data: existingRole, error: roleCheckError } = await supabase
      .from('user_roles')
      .select('*')
      .eq('user_id', user.id)
      .eq('role', 'admin')
      .single();
    
    if (existingRole) {
      return { success: true, message: "User is already an admin" };
    }
    
    // Add admin role
    const { error: insertError } = await supabase
      .from('user_roles')
      .insert({
        user_id: user.id,
        role: 'admin'
      });
    
    if (insertError) {
      console.error("Error setting user as admin:", insertError);
      return { success: false, message: "Failed to set user as admin" };
    }
    
    return {
      success: true,
      message: `${user.full_name} has been set as admin`
    };
    
  } catch (error) {
    console.error("Error in setUserAsAdmin:", error);
    return { success: false, message: "An unexpected error occurred" };
  }
};

// Check if a specific user is an admin
export const checkAdminStatus = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('*')
      .eq('user_id', userId)
      .eq('role', 'admin')
      .maybeSingle();
    
    if (error && error.code !== "PGRST116") {
      throw error;
    }
    
    return !!data;
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
};

// Approve premium subscription manually
export const approvePremiumSubscription = async (userId: string, duration: 'month' | 'year') => {
  try {
    const endDate = new Date();
    if (duration === 'month') {
      endDate.setMonth(endDate.getMonth() + 1);
    } else {
      endDate.setFullYear(endDate.getFullYear() + 1);
    }
    
    // Update or create subscription
    const { error } = await supabase
      .from('subscribers')
      .upsert({
        user_id: userId,
        subscribed: true,
        subscription_end: endDate.toISOString(),
        subscription_tier: 'premium',
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });
    
    if (error) throw error;
    
    return { success: true, message: `Premium subscription approved for ${duration}` };
  } catch (error) {
    console.error("Error approving subscription:", error);
    return { success: false, message: "Failed to approve subscription" };
  }
};

// Approve manual payment request
export const approveManualPayment = async (requestId: string) => {
  try {
    const { data: request, error: fetchError } = await supabase
      .from('manual_payment_requests')
      .select('*')
      .eq('id', requestId)
      .single();
    
    if (fetchError) throw fetchError;
    
    // Update payment request status
    const { error: updateError } = await supabase
      .from('manual_payment_requests')
      .update({
        status: 'approved',
        processed_at: new Date().toISOString()
      })
      .eq('id', requestId);
    
    if (updateError) throw updateError;
    
    // Create subscription based on the payment
    const endDate = new Date();
    if (request.billing_cycle === 'monthly') {
      endDate.setMonth(endDate.getMonth() + 1);
    } else {
      endDate.setFullYear(endDate.getFullYear() + 1);
    }
    
    await supabase
      .from('subscribers')
      .upsert({
        user_id: request.user_id,
        subscribed: true,
        subscription_end: endDate.toISOString(),
        subscription_tier: 'premium',
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });
    
    return { success: true, message: "Payment approved and subscription activated" };
  } catch (error) {
    console.error("Error approving payment:", error);
    return { success: false, message: "Failed to approve payment" };
  }
};
